import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import logo from '../logo.png';

const API = 'https://api.cpms-srlkevcharge.com';

// ── Icons ─────────────────────────────────────────────────────────────────
const IconChargingPump = () => (
  <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <rect x="4" y="3" width="10" height="18" rx="1.5" />
    <line x1="4" y1="9" x2="14" y2="9" />
    <circle cx="9" cy="6" r="1" fill="currentColor" stroke="none" />
    <path d="M14 8h2a2 2 0 012 2v7a1.5 1.5 0 003 0v-8l-2-2" />
  </svg>
);
const IconLock = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);
const IconCheck = () => (
  <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconAlert = () => (
  <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

function StateScreen({ type, title, message }) {
  return (
    <div className="pay-state-screen">
      <div>
        <div className={`pay-state-icon ${type}`}>
          {type === 'success' ? <IconCheck /> : <IconAlert />}
        </div>
        <h2>{title}</h2>
        <p>{message}</p>
      </div>
    </div>
  );
}

function CheckoutForm({ paymentInfo }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [status, setStatus] = useState('form');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }

    setStatus('success');
    setSubmitting(false);
  };

  if (status === 'success') {
    return (
      <StateScreen
        type="success"
        title="Payment held successfully"
        message="Your charging session is starting now."
      />
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {errorMessage && <div className="pay-error-banner">{errorMessage}</div>}

      <div className="pay-element-wrap">
        <PaymentElement />
      </div>

      <button type="submit" className="pay-submit-btn" disabled={!stripe || submitting}>
        {submitting ? 'Processing...' : `Hold £${paymentInfo.hold_amount.toFixed(2)} and Start Charging`}
      </button>

      <div className="pay-secure-note">
        <IconLock /> Secured by Stripe
      </div>
    </form>
  );
}

export default function ChargerPay() {
  const { qrCodeId } = useParams();
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payment/charger/${qrCodeId}/init`)
      .then(res => {
        setPaymentInfo(res.data);
        setStripePromise(loadStripe(res.data.publishable_key));
      })
      .catch(err => {
        setInitError(err.response?.data?.detail || 'Unable to load this charger. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [qrCodeId]);

  if (loading) {
    return (
      <div className="pay-page">
        <div className="pay-card">
          <div className="pay-body" style={{ textAlign: 'center', padding: '60px 24px' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading charger details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <StateScreen
        type="error"
        title="Unable to Start Payment"
        message={initError}
      />
    );
  }

  const options = {
    clientSecret: paymentInfo.client_secret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#1f9a4c',
        colorBackground: '#ffffff',
        colorText: '#1d2939',
        borderRadius: '10px',
        fontFamily: 'Outfit, Inter, sans-serif',
      },
    },
  };

  return (
    <div className="pay-page">
      <div className="pay-card">
        <div className="pay-header">
          <img src={logo} alt="SRLK EV Charge" className="pay-header-logo" />
          <h1>{paymentInfo.station_name}</h1>
          <p>Ready to charge your vehicle</p>
        </div>

        <div className="pay-body">
          <div className="pay-summary">
            <div className="pay-summary-row">
              <span className="pay-summary-label">Rate</span>
              <span className="pay-summary-value">{paymentInfo.currency} {paymentInfo.tariff_rate} {paymentInfo.tariff_unit}</span>
            </div>
            <div className="pay-summary-hold">
              <span className="pay-summary-hold-label">Amount to hold</span>
              <span className="pay-summary-hold-value">£{paymentInfo.hold_amount.toFixed(2)}</span>
            </div>
          </div>

          <p className="pay-explainer">
            This amount is held, not charged. You will only be billed for what you actually use.
          </p>

          <Elements stripe={stripePromise} options={options}>
            <CheckoutForm paymentInfo={paymentInfo} />
          </Elements>
        </div>
      </div>
    </div>
  );
}